import React, { useState } from 'react';
import nubiecloudMark from '../assets/nubiecloud-mark.png';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import apiService from '../services/api';
import { Button, Input } from '../components/ui';
import Logger from '../utils/logger';

interface ForgotPasswordForm {
  email: string;
}

const ForgotPasswordPage: React.FC = () => {
  const { t } = (useTranslation as any)('auth');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm<ForgotPasswordForm>();

  const onSubmit = async (data: ForgotPasswordForm) => {
    setLoading(true);
    try {
      const result = await apiService.requestPasswordReset({
        email: data.email
      });

      toast.success(result.message);
      setEmailSent(true);
      setSentEmail(data.email);
    } catch (error: unknown) {
      Logger.error('Erreur demande reset:', error);
      const apiError = error as { response?: { data?: { detail?: string } } };
      const errorMessage = apiError.response?.data?.detail || (t('resetPassword.error') as string);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-surface-0 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <div className="flex items-center space-x-2">
              <img src={nubiecloudMark} alt="" className="w-8 h-8 object-contain" />
              <span className="text-2xl font-bold text-text-primary">NubieCloud</span>
            </div>
          </div>

          <div className="mt-8 bg-surface-2 py-8 px-4 shadow-card sm:rounded-card sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-500/12">
                <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h2 className="mt-4 text-lg font-medium text-text-primary">
                {t('forgotPassword.successTitle')}
              </h2>
              <p className="mt-2 text-sm text-text-secondary">
                {t('forgotPassword.successMessage')}{' '}
                <span className="font-medium text-accent-400">{sentEmail}</span>
              </p>
              <p className="mt-2 text-xs text-text-tertiary">
                {t('forgotPassword.successExpiry')}
              </p>

              <div className="mt-6">
                <Link
                  to="/login"
                  className="text-accent-400 hover:text-accent-300 text-sm font-medium"
                >
                  {t('forgotPassword.backToLogin')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-0 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="flex items-center space-x-2">
            <img src={nubiecloudMark} alt="" className="w-8 h-8 object-contain" />
            <span className="text-2xl font-bold text-text-primary">NubieCloud</span>
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-text-primary">
          {t('forgotPassword.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-text-secondary">
          {t('forgotPassword.description')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface-2 py-8 px-4 shadow-card sm:rounded-card sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {/* Email */}
            <Input
              {...register('email', {
                required: t('email.required', { ns: 'validation' }),
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('email.invalid', { ns: 'validation' })
                }
              })}
              id="email"
              type="email"
              label={t('forgotPassword.email')}
              autoComplete="email"
              placeholder={t('forgotPassword.emailPlaceholder')}
              error={errors.email?.message}
            />

            {/* Submit Button */}
            <div>
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={loading}
                disabled={loading}
                className="w-full"
              >
                {loading ? t('forgotPassword.submitting') : t('forgotPassword.submit')}
              </Button>
            </div>

            {/* Retour */}
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-text-secondary hover:text-text-primary"
              >
                {t('forgotPassword.backToLogin')}
              </Link>
            </div>
          </form>

          {/* Info */}
          <div className="mt-6 p-4 bg-surface-3 rounded-md border border-border-1">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-text-tertiary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p
                  className="text-sm text-text-secondary"
                  dangerouslySetInnerHTML={{ __html: t('forgotPassword.rateLimitInfo') }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
